/**
 * Input filtering rules for form fields.
 * Each function returns the cleaned value to store in state.
 */

export const normalizeDigits = (value) =>
  String(value ?? '')
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)));

/** Arabic letters + spaces only */
export const filterArabic = (value) => String(value ?? '').replace(/[^\u0600-\u06FF\s]/g, '');

/** English letters + spaces only */
export const filterEnglish = (value) => String(value ?? '').replace(/[^a-zA-Z\s]/g, '');

/** Digits only (no decimal, no sign) */
export const filterDigits = (value) => normalizeDigits(value).replace(/\D/g, '');

/** Digits + optional leading + sign (for phone with country code) */
export const filterPhone = (value) => {
  const cleaned = normalizeDigits(value).replace(/[^0-9+]/g, '');
  return cleaned.replace(/(?!^)\+/g, '');
};

/**
 * Positive decimal number — no negative sign, digits and at most one dot.
 * e.g. "12.50" ✓ | "-5" ✗ | "1.2.3" → "1.23"
 */
export const filterPositiveDecimal = (value) => {
  const noNeg = normalizeDigits(value).replace(/-/g, '');
  const onlyValid = noNeg.replace(/[^0-9.]/g, '');
  const parts = onlyValid.split('.');
  return parts.length > 2
    ? parts[0] + '.' + parts.slice(1).join('')
    : onlyValid;
};

export const filterPositiveInteger = (value) => filterDigits(value);

/**
 * Central field filter — returns filtered value based on field name.
 * Drop this into any handleInputChange before setFormData.
 *
 * Usage:
 *   const finalValue = type === 'checkbox' ? checked : applyFieldFilter(name, value);
 *   setFormData(prev => ({ ...prev, [name]: finalValue }));
 */
export const applyFieldFilter = (name, value) => {
  const fieldName = String(name || '').toLowerCase();

  if (fieldName.endsWith('_ar') || fieldName.includes('arabic')) {
    return filterArabic(value);
  }

  if (fieldName.endsWith('_en') || fieldName.includes('english')) {
    return filterEnglish(value);
  }

  switch (fieldName) {
    // ── Arabic text ──
    case 'name_ar':
    case 'name_ar_1':
    case 'name_ar_2':
      return filterArabic(value);

    // ── English text ──
    case 'name_en':
    case 'name_en_1':
    case 'name_en_2':
      return filterEnglish(value);

    // ── Phone ──
    case 'phone':
    case 'mobile':
    case 'phone_number':
      return filterPhone(value);

    // ── Integer IDs / codes ──
    case 'national_id':
    case 'national_number':
    case 'id_number':
    case 'zip_code':
    case 'postal_code':
    case 'account_number':
      return filterDigits(value);

    // ── Positive financial / numeric amounts ──
    case 'amount':
    case 'total':
    case 'total_amount':
    case 'price':
    case 'unit_price':
    case 'cost':
    case 'value':
    case 'contract_value':
    case 'rate':
    case 'exchange_rate':
    case 'tax_rate':
    case 'quantity':
    case 'qty':
    case 'salary':
    case 'basic_salary':
    case 'net_salary':
    case 'bonus':
    case 'deduction':
    case 'debit':
    case 'credit':
    case 'balance':
    case 'loan_amount':
    case 'monthly_installment':
    case 'penalty_amount':
      return filterPositiveDecimal(value);

    default:
      return normalizeDigits(value);
  }
};

export const getGuardedInputValue = (target, value = target?.value) => {
  if (!target) return value;

  const fieldName = target.name || target.id || '';
  const type = String(target.type || '').toLowerCase();

  if (type === 'number' || target.inputMode === 'numeric' || target.inputMode === 'decimal') {
    const step = target.getAttribute?.('step');
    const shouldAllowDecimal = step === 'any' || (step && String(step).includes('.')) || /\./.test(String(value));
    return shouldAllowDecimal ? filterPositiveDecimal(value) : filterPositiveInteger(value);
  }

  return applyFieldFilter(fieldName, value);
};

/**
 * Returns true if password and confirmation don't match (and both are non-empty).
 */
export const isPasswordMismatch = (password, confirmation) =>
  confirmation.length > 0 && confirmation !== password;
